import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3, Users, FileText, Receipt,
  ShieldCheck, Mail, TrendingUp, Settings as SettingsIcon,
  LogOut, Zap, Play, Bot, BookOpen, ClipboardList, Clock
} from 'lucide-react';
import FlowRCMLogo from './FlowRCMLogo';
import NotificationBell from './NotificationBell';
import DemoTour from './DemoTour';
import DemoChatWidget from './DemoChatWidget';
import { useAuth, useDemoTour } from '../contexts';
import * as apiService from '../services/apiService';
import { setSelectedPracticeId } from '../services/awsApiService';

const navItems = [
  { label: 'Claim Robot',    href: '/',               icon: Bot,         exact: true, ai: true },
  { label: 'Patients',       href: '/patients',        icon: Users,       ai: true },
  { label: 'Claims Pipeline',href: '/claims',          icon: FileText,    ai: true },
  { label: 'ERA Posting',    href: '/era-posting',     icon: Receipt },
  { label: 'Pre-Auth',       href: '/preauth',         icon: ShieldCheck, ai: true },
  { label: 'Statements',     href: '/statements',      icon: Mail },
  { label: 'Performance',    href: '/performance',     icon: TrendingUp },
  { label: 'A/R Aging',      href: '/ar-aging',        icon: Clock },
  { label: 'Activity Log',   href: '/audit-log',       icon: ClipboardList },
];

const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, practice, user, managedPractices, selectedPracticeId, switchPractice } = useAuth();
  const { startTour, isActive: tourActive } = useDemoTour();
  const appMode = apiService.getAppMode();

  const handleSwitchPractice = (practiceId) => {
    setSelectedPracticeId(practiceId);
    switchPractice(practiceId);
    navigate('/');
  };

  const isActive = (item) => {
    if (item.href === '/') return location.pathname === '/' && !location.search;
    if (item.href.startsWith('/?tab=')) return location.pathname === '/' && location.search === item.href.replace('/', '');
    return location.pathname === item.href;
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* Teal Header */}
      <header className="bg-gradient-to-r from-teal-900 to-teal-800 text-white shadow-lg flex-shrink-0" style={{ zIndex: 50 }}>
        <div className="pl-3 pr-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <FlowRCMLogo size="default" />
            </div>
            <div className="flex items-center gap-4">
              {appMode === 'demo' && (
                <>
                  <div className="flex items-center gap-2 bg-teal-800/50 px-3 py-1.5 rounded-lg">
                    <Zap className="w-4 h-4 text-yellow-300" />
                    <span className="text-sm font-semibold">Demo</span>
                  </div>
                  <button
                    onClick={startTour}
                    disabled={tourActive}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: tourActive ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #14b8a6, #0f766e)',
                      border: '1px solid rgba(255,255,255,0.25)', color: '#fff',
                      padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: tourActive ? 'default' : 'pointer', opacity: tourActive ? 0.6 : 1,
                      boxShadow: tourActive ? 'none' : '0 2px 8px rgba(0,0,0,0.25)',
                    }}
                  >
                    <Play size={13} />
                    Watch Demo
                  </button>
                </>
              )}
              <NotificationBell />
            </div>
          </div>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside className="bg-gradient-to-b from-teal-900 to-teal-800 text-white flex-shrink-0 flex flex-col" style={{ width: 210, overflowY: 'auto' }}>

          {/* Practice name */}
          {(practice?.name || appMode === 'demo') && (
            <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {practice?.name && appMode !== 'demo' && (
                <div style={{ color: '#99f6e4', fontSize: 12, fontWeight: 500 }}>{practice.name}</div>
              )}
              {appMode === 'demo' && (
                <span style={{ background: '#854d0e', color: '#fef08a', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>Demo</span>
              )}
            </div>
          )}

          {/* Practice Switcher */}
          {(managedPractices.length > 1 || appMode === 'demo') && (
            <div data-tour="practice-switcher" style={{ padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Active Practice</div>
              <select
                value={selectedPracticeId || (appMode === 'demo' ? 'demo-loc-1' : '')}
                onChange={e => appMode !== 'demo' && handleSwitchPractice(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                {appMode === 'demo' ? (
                  <>
                    <option value="demo-loc-1" style={{ background: '#0f766e', color: '#fff' }}>Bright Smiles — Location 1</option>
                    <option value="demo-loc-2" style={{ background: '#0f766e', color: '#fff' }}>Bright Smiles — Location 2</option>
                  </>
                ) : managedPractices.map(p => (
                  <option key={p.id} value={p.id} style={{ background: '#0f766e', color: '#fff' }}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Nav items */}
          <nav style={{ padding: '8px 8px', flex: 1 }}>
            {navItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <button
                  key={item.label}
                  onClick={() => navigate(item.href)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '8px 10px', marginBottom: 1,
                    background: active ? 'rgba(255,255,255,0.15)' : 'none',
                    border: 'none', borderRadius: 7,
                    color: active ? '#ffffff' : '#99f6e4',
                    fontWeight: active ? 600 : 400, fontSize: 13,
                    cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'none'; }}
                >
                  <Icon size={15} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.ai && <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>AI</span>}
                </button>
              );
            })}
          </nav>

          {/* Bottom: user + settings + logout */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                {initials}
              </div>
              <div style={{ fontSize: 11, color: '#99f6e4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email || 'User'}
              </div>
            </div>
            {[
              { icon: SettingsIcon, label: 'Settings',    onClick: () => navigate('/settings'),  color: '#99f6e4' },
              { icon: BookOpen,     label: 'Setup Guide', onClick: () => navigate('/?setup=1'),  color: '#99f6e4' },
              { icon: LogOut,       label: 'Logout',      onClick: () => { signOut(); navigate('/login'); }, color: '#fca5a5' },
            ].map(({ icon: Icon, label, onClick, color }) => (
              <button
                key={label}
                onClick={onClick}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '7px 10px', background: 'none', border: 'none', borderRadius: 7, color, fontSize: 13, cursor: 'pointer', marginBottom: 1 }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <Icon size={15} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
          {children}
        </main>

      </div>

      {appMode === 'demo' && <DemoTour />}
      {appMode === 'demo' && <DemoChatWidget />}
    </div>
  );
};

export default AppLayout;
